// Typen, Auslöser und mobile Variante (Sheet von unten) des AccountMenu.
import { type Icon as PhosphorIcon, SignOut, UserPlus } from '@phosphor-icons/react';
import { type ComponentPropsWithRef, type ReactNode } from 'react';
import { Avatar } from '../kern/Avatar';
import { cn } from '../kern/cn';
import { ListMenu, type ListMenuItem, type ListMenuSection } from './ListMenu';
import { Drawer } from './parts';
import { ThemeToggle } from './ThemeToggle';

export interface AccountMenuPerson {
  id: string;
  name: string;
  email: string;
  avatarSrc?: string | null;
  /** Rollen als Chip-Texte („Line manager“, „Admin“). */
  roles: readonly string[];
}

export interface AccountMenuAccount {
  id: string;
  /** Schlüssel der Avatar-Farbe (Person-ID wie im Kopf); Standard `id`. */
  avatarId?: string;
  name: string;
  email: string;
  avatarSrc?: string | null;
  active?: boolean;
  expired?: boolean;
}

export interface AccountMenuEntry {
  id: string;
  label: string;
  /** account: Konto-Einträge (Account settings …) · platforms: Plattform-Wechsel · help. */
  group: 'account' | 'platforms' | 'help';
  icon?: PhosphorIcon;
  /** Eigenes Bild links (Plattform-Monogramm). */
  leading?: ReactNode;
  trailing?: ReactNode;
  href?: string;
  reload?: boolean;
  onSelect?: () => void;
}

export interface AccountMenuProps {
  person: AccountMenuPerson;
  /** Angemeldete Konten (Kontowechsel); leer = Abschnitt fehlt. */
  accounts?: readonly AccountMenuAccount[];
  entries?: readonly AccountMenuEntry[];
  onSwitchAccount?: (accountId: string) => void;
  /** Ohne Angabe fehlt „Add another account“ (Modus supabase, P2). */
  onAddAccount?: () => void;
  onSignOut: () => void;
  onSignOutAll?: () => void;
  showTheme?: boolean;
  /** `avatar`: runder Avatar 36 (Topbar) · `row`: Avatar-Zeile mit Name und Rolle (Navigationsfuß). */
  trigger?: 'avatar' | 'row';
  align?: 'start' | 'end';
  side?: 'top' | 'bottom' | 'right';
}

/** Rollen-Chips im Kopf des Menüs (Desktop und mobil gleich). */
export function RoleChips({ roles }: { roles: readonly string[] }) {
  if (roles.length === 0) return null;
  return (
    <p className="mt-1 flex flex-wrap gap-1">
      {roles.map((role) => (
        <span
          key={role}
          className="inline-flex h-5 items-center rounded-full bg-chip-bg px-2 text-chip text-chip-fg"
        >
          {role}
        </span>
      ))}
    </p>
  );
}

/** Auslöser: Avatar 36 bzw. Zeile 56 (AdminNav-, AiSidebar-Fuß). Nimmt Radix-Trigger-Props an. */
export function AccountTrigger({
  person,
  trigger = 'avatar',
  ref,
  ...rest
}: AccountMenuProps & Omit<ComponentPropsWithRef<'button'>, keyof AccountMenuProps>) {
  const {
    accounts: _a,
    entries: _e,
    onSwitchAccount: _s,
    onAddAccount: _ad,
    onSignOut: _o,
    onSignOutAll: _oa,
    showTheme: _t,
    align: _al,
    side: _si,
    ...buttonProps
  } = rest as AccountMenuProps & ComponentPropsWithRef<'button'>;
  if (trigger === 'row') {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={`Account menu, ${person.name}`}
        className="flex h-14 w-full min-w-0 items-center gap-3 rounded-lg px-2 text-left transition-colors duration-(--bar-dur-fast) hover:bg-card-hover data-[state=open]:bg-card-hover"
        {...buttonProps}
      >
        <Avatar name={person.name} id={person.id} src={person.avatarSrc} size={32} decorative />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-label font-medium text-fg">{person.name}</span>
          {person.roles[0] ? (
            <span className="truncate text-small text-fg-secondary">{person.roles[0]}</span>
          ) : null}
        </span>
      </button>
    );
  }
  return (
    <button
      ref={ref}
      type="button"
      aria-label={`Account menu, ${person.name}`}
      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full transition-transform duration-(--bar-dur-instant) active:scale-97"
      {...buttonProps}
    >
      <Avatar name={person.name} id={person.id} src={person.avatarSrc} size={36} decorative />
    </button>
  );
}

/** Mobil: Sheet von unten mit denselben Einträgen als Zeilen. */
export function MobileAccountSheet(
  props: AccountMenuProps & { open: boolean; onOpenChange: (open: boolean) => void },
) {
  const { person, accounts = [], entries = [], open, onOpenChange } = props;
  const close = () => onOpenChange(false);
  const toItem = (entry: AccountMenuEntry): ListMenuItem => ({
    id: entry.id,
    label: entry.label,
    icon: entry.icon,
    leading: entry.leading,
    trailing: entry.trailing,
    href: entry.href,
    reload: entry.reload,
    onSelect: entry.onSelect,
  });
  const sections: ListMenuSection[] = [];
  if (accounts.length > 0) {
    sections.push({
      id: 'accounts',
      label: 'Accounts',
      items: [
        ...accounts.map((account) => ({
          id: account.id,
          label: account.name,
          description: account.expired ? 'Session expired · Sign in' : account.email,
          leading: (
            <Avatar
              name={account.name}
              id={account.avatarId ?? account.id}
              size={28}
              surface="card"
              decorative
            />
          ),
          active: account.active,
          onSelect: account.active ? undefined : () => props.onSwitchAccount?.(account.id),
        })),
        ...(props.onAddAccount
          ? [
              {
                id: 'add',
                label: 'Add another account',
                icon: UserPlus,
                onSelect: props.onAddAccount,
              },
            ]
          : []),
      ],
    });
  }
  sections.push({ id: 'account', items: entries.filter((e) => e.group === 'account').map(toItem) });
  const platforms = entries.filter((e) => e.group === 'platforms').map(toItem);
  if (platforms.length) sections.push({ id: 'platforms', label: 'Platforms', items: platforms });
  const help = entries.filter((e) => e.group === 'help').map(toItem);
  if (help.length) sections.push({ id: 'help', items: help });
  const signOut: ListMenuItem[] = [
    { id: 'signout', label: 'Sign out', icon: SignOut, onSelect: props.onSignOut },
  ];
  if (accounts.length > 1 && props.onSignOutAll)
    signOut.push({
      id: 'signout-all',
      label: 'Sign out of all accounts',
      icon: SignOut,
      onSelect: props.onSignOutAll,
    });

  return (
    <>
      <AccountTrigger
        {...props}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => onOpenChange(true)}
      />
      <Drawer open={open} onOpenChange={onOpenChange} side="bottom" title="Account">
        <div className="flex items-center gap-3 px-5 pt-3 pb-2">
          <Avatar
            name={person.name}
            id={person.id}
            src={person.avatarSrc}
            size={40}
            surface="card"
            decorative
          />
          <div className="min-w-0">
            <p className="truncate text-body font-semibold text-fg">{person.name}</p>
            <p className="truncate text-label text-fg-secondary">{person.email}</p>
            <RoleChips roles={person.roles} />
          </div>
        </div>
        <div className="px-2 pb-4">
          <ListMenu sections={sections.slice(0, accounts.length > 0 ? 2 : 1)} onDone={close} />
          {props.showTheme === false ? null : (
            <div className={cn('flex min-h-12 items-center justify-between gap-3 px-3')}>
              <span className="text-body text-fg">Theme</span>
              <ThemeToggle size="sm" />
            </div>
          )}
          <ListMenu
            sections={[
              ...sections.slice(accounts.length > 0 ? 2 : 1),
              { id: 'out', items: signOut },
            ]}
            onDone={close}
          />
        </div>
      </Drawer>
    </>
  );
}
